import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Layers, Wrench, Paintbrush2, ScanLine } from 'lucide-react';
import { Button, Spinner } from '@crc-evans/ui-components';
import { message } from 'antd';
import axiosConfig from '../../services/axiosConfig';

const NDT_TYPE_OPTIONS = [
  { value: 'X-Ray', label: 'X-Ray' },
  { value: 'AUT', label: 'AUT (Automated Ultrasonic Testing)' },
  { value: 'Both', label: 'Both (X-Ray & AUT)' },
];

const StationTag = ({ number, onRemove, disabled }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      background: '#EFF6FF',
      border: '1px solid #BFDBFE',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: 500,
      color: '#1D4ED8',
    }}
  >
    Station {number}
    {!disabled && (
      <button
        type="button"
        onClick={() => onRemove(number)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0 0 0 4px',
          color: '#93C5FD',
          display: 'flex',
          alignItems: 'center',
        }}
        aria-label={`Remove station ${number}`}
      >
        <Trash2 size={12} />
      </button>
    )}
  </span>
);

const StationSection = ({ title, icon: Icon, color, bgColor, stations, onAdd, onRemove, ndtType, onNdtTypeChange, isNDT, disabled }) => {
  const [inputVal, setInputVal] = useState('');

  const handleAdd = () => {
    const num = inputVal.trim();
    if (!num) return;
    if (isNaN(Number(num))) {
      message.warning('Please enter a valid station number.');
      return;
    }
    const n = Number(num);
    if (stations.includes(n)) {
      message.warning(`Station ${n} is already added.`);
      return;
    }
    onAdd(n);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div
          style={{
            background: bgColor,
            color: color,
            padding: '8px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={18} />
        </div>
        <div>
          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1E293B' }}>{title}</h4>
          <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>
            {stations.length} station{stations.length !== 1 ? 's' : ''} defined
          </p>
        </div>
      </div>

      {isNDT && (
        <div style={{ marginBottom: '14px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            NDT Type
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {NDT_TYPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  border: `2px solid ${ndtType === opt.value ? '#3B82F6' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  background: ndtType === opt.value ? '#EFF6FF' : '#FAFAFA',
                  fontSize: '13px',
                  fontWeight: ndtType === opt.value ? 600 : 400,
                  color: ndtType === opt.value ? '#1D4ED8' : '#374151',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="ndtType"
                  value={opt.value}
                  checked={ndtType === opt.value}
                  onChange={() => !disabled && onNdtTypeChange(opt.value)}
                  disabled={disabled}
                  style={{ accentColor: '#3B82F6' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {!disabled && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <input
            type="number"
            min={1}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Station number"
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #D1D5DB',
              fontSize: '14px',
              outline: 'none',
              color: '#1F2937',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
            onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
          />
          <button
            type="button"
            onClick={handleAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 14px',
              background: '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            <Plus size={16} /> Add
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '32px' }}>
        {stations.length === 0 ? (
          <span style={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>
            No stations defined yet.
          </span>
        ) : (
          [...stations].sort((a, b) => a - b).map((n) => (
            <StationTag
              key={n}
              number={n}
              onRemove={onRemove}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
};

const DPRSettingsProductionLineTab = ({ selectedProject }) => {
  const [totalStations, setTotalStations] = useState('');
  const [weldingStations, setWeldingStations] = useState([]);
  const [coatingStations, setCoatingStations] = useState([]);
  const [ndtStations, setNdtStations] = useState([]);
  const [ndtType, setNdtType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;
    setIsLoading(true);
    axiosConfig
      .get(`/production-line-setup?projectId=${projectId}`)
      .then((res) => {
        const d = res.data?.data ?? res.data;
        if (d) {
          setTotalStations(d.totalStations != null ? String(d.totalStations) : '');
          setWeldingStations(Array.isArray(d.weldingStations) ? d.weldingStations : []);
          setCoatingStations(Array.isArray(d.coatingStations) ? d.coatingStations : []);
          setNdtStations(Array.isArray(d.ndtStations) ? d.ndtStations : []);
          setNdtType(d.ndtType ?? '');
        }
      })
      .catch(() => {
        /* No setup yet — start fresh */
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  const handleSave = async () => {
    if (!projectId) return;
    if (!totalStations || isNaN(Number(totalStations)) || Number(totalStations) < 1) {
      message.warning('Enter a valid total number of stations.');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        projectId: Number(projectId),
        totalStations: Number(totalStations),
        weldingStations,
        coatingStations,
        ndtStations,
        ndtType: ndtType || undefined,
      };
      await axiosConfig.put('/production-line-setup', payload);
      message.success('Production line setup saved successfully.');
    } catch (err) {
      console.error('Error saving production line setup:', err);
      message.error('Failed to save production line setup.');
    } finally {
      setIsSaving(false);
    }
  };

  const addStation = (setter) => (n) => setter((prev) => [...prev, n]);
  const removeStation = (setter) => (n) => setter((prev) => prev.filter((s) => s !== n));

  if (!selectedProject) {
    return (
      <div style={{ padding: '24px', color: '#64748B' }}>
        Select a project to configure the production line.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '28px' }}>
        <div
          style={{
            background: '#F0FDF4',
            color: '#16A34A',
            padding: '10px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Layers size={24} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0F172A' }}>
            Production Line Set-Up
          </h3>
          <p style={{ margin: '4px 0 0 0', color: '#64748B', fontSize: '14px' }}>
            Define stations, their types, and NDT configuration for this project.
          </p>
        </div>
      </div>

      {/* Total Stations */}
      <div style={{ marginBottom: '24px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '8px',
          }}
        >
          Total Number of Stations <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <input
          type="number"
          min={1}
          value={totalStations}
          onChange={(e) => setTotalStations(e.target.value)}
          placeholder="e.g. 10"
          style={{
            width: '200px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #D1D5DB',
            fontSize: '14px',
            outline: 'none',
            color: '#1F2937',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
          onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
        />
        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#94A3B8' }}>
          Enter the total count, then assign each station below by type.
        </p>
      </div>

      {/* Station Sections */}
      <StationSection
        title="Welding Stations"
        icon={Wrench}
        color="#D97706"
        bgColor="#FFFBEB"
        stations={weldingStations}
        onAdd={addStation(setWeldingStations)}
        onRemove={removeStation(setWeldingStations)}
      />

      <StationSection
        title="Coating Stations"
        icon={Paintbrush2}
        color="#7C3AED"
        bgColor="#F5F3FF"
        stations={coatingStations}
        onAdd={addStation(setCoatingStations)}
        onRemove={removeStation(setCoatingStations)}
      />

      <StationSection
        title="NDT Stations"
        icon={ScanLine}
        color="#0891B2"
        bgColor="#ECFEFF"
        stations={ndtStations}
        onAdd={addStation(setNdtStations)}
        onRemove={removeStation(setNdtStations)}
        isNDT
        ndtType={ndtType}
        onNdtTypeChange={setNdtType}
      />

      {/* Save */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #F1F5F9',
          marginTop: '8px',
        }}
      >
        <Button
          label={isSaving ? 'Saving...' : 'Save'}
          variant="secondary"
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </div>
  );
};

export default DPRSettingsProductionLineTab;
